import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  GitBranch,
  GitCommitHorizontal,
  GitPullRequestArrow,
  Loader2,
  RefreshCw,
  Tags,
  Workflow,
} from 'lucide-react'
import GitHistoryGraph from './GitHistoryGraph'
import {
  getGitHistory,
  openRepoBranchMergeRequest,
  openRepoBranchPullRequest,
  type GitHistoryCommit,
  type GitHistoryProviderBranch,
  type GitHistoryResponse,
  type RepoBinding,
} from '../../lib/api'
import { formatRelativeTime } from '../../lib/format'

const TIMELINE_PAGE_SIZE = 25

function formatAbsoluteTime(value: string | null | undefined): string {
  if (!value) return 'Unknown'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

function decorationBadgeClass(label: string): string {
  if (label.startsWith('HEAD')) return 'border-blue-500/30 bg-blue-500/10 text-blue-500'
  if (label.startsWith('tag:')) return 'border-amber-500/30 bg-amber-500/10 text-amber-500'
  if (label.startsWith('origin/') || label.includes('/')) return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
  return 'border-border bg-secondary text-muted-foreground'
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'success':
    case 'synced':
      return 'border-green-500/30 bg-green-500/10 text-green-500'
    case 'queued':
    case 'running':
      return 'border-blue-500/30 bg-blue-500/10 text-blue-500'
    case 'failed':
    case 'error':
      return 'border-red-500/30 bg-red-500/10 text-red-500'
    default:
      return 'border-border bg-secondary text-muted-foreground'
  }
}

function providerBranchBadgeClass(branch: GitHistoryProviderBranch): string {
  if (branch.default) return 'border-blue-500/30 bg-blue-500/10 text-blue-500'
  if (branch.protected) return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
  return 'border-border bg-secondary text-muted-foreground'
}

function isAncestorCommit(
  candidateSha: string | null | undefined,
  descendantSha: string | null | undefined,
  commitsBySha: Map<string, GitHistoryCommit>,
): boolean {
  if (!candidateSha || !descendantSha) return false
  if (candidateSha === descendantSha) return true

  const visited = new Set<string>()
  const stack = [descendantSha]
  while (stack.length > 0) {
    const sha = stack.pop()
    if (!sha || visited.has(sha)) continue
    visited.add(sha)
    if (sha === candidateSha) return true
    const commit = commitsBySha.get(sha)
    if (commit) stack.push(...commit.parents)
  }
  return false
}

interface GitHistoryExplorerProps {
  appName: string
  binding?: RepoBinding | null
  initialLimit?: number
}

export default function GitHistoryExplorer({
  appName,
  binding = null,
  initialLimit = 80,
}: GitHistoryExplorerProps) {
  const [limit, setLimit] = useState(initialLimit)
  const [history, setHistory] = useState<GitHistoryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [selectedSha, setSelectedSha] = useState<string | null>(null)
  const [activeView, setActiveView] = useState<'graph' | 'timeline'>('graph')
  const [branchAction, setBranchAction] = useState<string | null>(null)
  const [timelinePage, setTimelinePage] = useState(1)

  const loadHistory = useCallback(async (requestedLimit: number = limit) => {
    setLoading(true)
    setError(null)
    try {
      const result = await getGitHistory(appName, requestedLimit)
      setHistory(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load git history')
    } finally {
      setLoading(false)
    }
  }, [appName, limit])

  useEffect(() => {
    void loadHistory(limit)
  }, [loadHistory, limit])

  const refCounts = useMemo(() => {
    const counts = { local: 0, remote: 0, tag: 0 }
    for (const ref of history?.refs || []) {
      if (ref.kind === 'local') counts.local += 1
      if (ref.kind === 'remote') counts.remote += 1
      if (ref.kind === 'tag') counts.tag += 1
    }
    return counts
  }, [history?.refs])

  const dirtyCount = history?.statusLines.length || 0
  const providerBranches = useMemo(() => history?.providerBranches || [], [history?.providerBranches])
  const providerBranchHeads = useMemo(() => {
    const bySha = new Map<string, GitHistoryProviderBranch[]>()
    for (const branch of providerBranches) {
      if (!branch.commitSha) continue
      const current = bySha.get(branch.commitSha) || []
      current.push(branch)
      bySha.set(branch.commitSha, current)
    }
    return bySha
  }, [providerBranches])
  const localRemoteBranchNames = useMemo(() => {
    const names = new Set<string>()
    for (const ref of history?.refs || []) {
      if (ref.kind !== 'remote') continue
      names.add(ref.name.replace(/^[^/]+\//, ''))
    }
    return names
  }, [history?.refs])
  const providerOnlyBranches = useMemo(
    () => providerBranches.filter((branch: GitHistoryProviderBranch) => !localRemoteBranchNames.has(branch.name)),
    [localRemoteBranchNames, providerBranches],
  )
  const baseBranchName = binding?.defaultBranch || binding?.trackedBranch || 'main'
  const commitsBySha = useMemo(
    () => new Map((history?.commits || []).map((commit) => [commit.sha, commit])),
    [history?.commits],
  )
  const baseBranchSha = useMemo(() => {
    const providerBase = providerBranches.find((branch: GitHistoryProviderBranch) => branch.name === baseBranchName)
    if (providerBase?.commitSha) return providerBase.commitSha
    return history?.currentBranch === baseBranchName && history ? history.headSha : null
  }, [baseBranchName, history, providerBranches])
  const reviewableBranchNames = useMemo(() => {
    const names = new Set<string>()
    if (!binding) return names
    for (const branch of providerBranches) {
      if (!branch.commitSha || branch.name === baseBranchName) continue
      if (isAncestorCommit(branch.commitSha, baseBranchSha, commitsBySha)) continue
      names.add(branch.name)
    }
    return names
  }, [baseBranchName, baseBranchSha, binding, commitsBySha, providerBranches])
  const reviewLabel = binding?.provider === 'gitlab' ? 'Open MR' : 'Open PR'
  const selectedCommit = useMemo(
    () => history?.commits.find((commit) => commit.sha === selectedSha) || history?.commits[0] || null,
    [history?.commits, selectedSha],
  )
  const timelinePageCount = Math.max(1, Math.ceil((history?.commits.length || 0) / TIMELINE_PAGE_SIZE))
  const timelinePageStart = (timelinePage - 1) * TIMELINE_PAGE_SIZE
  const timelineCommits = useMemo(
    () => (history?.commits || []).slice(timelinePageStart, timelinePageStart + TIMELINE_PAGE_SIZE),
    [history?.commits, timelinePageStart],
  )

  const handleOpenBranchReview = useCallback(async (branch: GitHistoryProviderBranch) => {
    if (!binding || !branch.commitSha) return

    setBranchAction(branch.name)
    setError(null)
    setNotice(null)
    try {
      if (binding.provider === 'gitlab') {
        const result = await openRepoBranchMergeRequest(appName, {
          base_branch: baseBranchName,
          head_branch: branch.name,
          draft: true,
        })
        const url = result.publication.mrUrl
        setNotice(url ? `Draft MR ready for ${branch.name}: ${url}` : `Draft MR ready for ${branch.name}`)
        if (url) window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        const result = await openRepoBranchPullRequest(appName, {
          base_branch: baseBranchName,
          head_branch: branch.name,
          draft: true,
        })
        const url = result.publication.prUrl
        setNotice(url ? `Draft PR ready for ${branch.name}: ${url}` : `Draft PR ready for ${branch.name}`)
        if (url) window.open(url, '_blank', 'noopener,noreferrer')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to open ${binding.provider === 'gitlab' ? 'MR' : 'PR'}`)
    } finally {
      setBranchAction(null)
    }
  }, [appName, baseBranchName, binding])

  useEffect(() => {
    if (!history?.commits.length) {
      setSelectedSha(null)
      return
    }
    if (!selectedSha || !history.commits.some((commit) => commit.sha === selectedSha)) {
      setSelectedSha(history.commits[0].sha)
    }
  }, [history?.commits, selectedSha])

  useEffect(() => {
    setTimelinePage((current) => Math.min(Math.max(current, 1), timelinePageCount))
  }, [timelinePageCount])

  return (
    <div className="min-w-0 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">Git History</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Branches, merges, refs, and commit metadata from the app workspace repository.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {history?.truncated && (
            <button
              type="button"
              onClick={() => setLimit((current) => Math.min(current + 80, 250))}
              className="rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-secondary"
            >
              Show More
            </button>
          )}
          <button
            type="button"
            onClick={() => void loadHistory(limit)}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-secondary disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {notice && (
        <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-600 dark:text-green-400">
          {notice}
        </div>
      )}

      {!error && loading && !history && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/20 p-4 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading git history…
        </div>
      )}

      {history && !history.isGitRepo && (
        <div className="rounded-lg border border-border bg-secondary/20 p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <AlertCircle className="w-4 h-4 text-muted-foreground" />
            Git history is not available yet
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {history.error || 'This app workspace does not have a git repository yet.'}
          </p>
        </div>
      )}

      {history?.isGitRepo && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
            <div className="min-w-0 rounded-lg border border-border bg-secondary/20 p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Current branch</p>
              <p className="mt-1 break-words font-medium">{history.currentBranch || 'Detached HEAD'}</p>
              {history.headSha && (
                <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{history.headSha.slice(0, 12)}</p>
              )}
            </div>
            <div className="min-w-0 rounded-lg border border-border bg-secondary/20 p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Refs</p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-1">
                  <GitBranch className="w-3 h-3" />
                  {refCounts.local} local
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-1">
                  <Workflow className="w-3 h-3" />
                  {refCounts.remote} remote
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-1">
                  <Tags className="w-3 h-3" />
                  {refCounts.tag} tags
                </span>
              </div>
            </div>
            <div className="min-w-0 rounded-lg border border-border bg-secondary/20 p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Worktrees</p>
              <p className="mt-1 font-medium">{history.worktrees.length}</p>
              {history.worktrees.length > 0 && (
                <p className="mt-1 break-words text-xs text-muted-foreground">
                  {history.worktrees
                    .slice(0, 2)
                    .map((worktree) => worktree.branch || worktree.displayPath)
                    .join(', ')}
                </p>
              )}
            </div>
            <div className="min-w-0 rounded-lg border border-border bg-secondary/20 p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Workspace state</p>
              <p className="mt-1 font-medium">{dirtyCount === 0 ? 'Clean' : `${dirtyCount} changed files`}</p>
              {binding && (
                <span className={`mt-2 inline-flex rounded-full border px-2 py-1 text-xs ${statusBadgeClass(binding.lastSyncStatus)}`}>
                  Sync: {binding.lastSyncStatus}
                </span>
              )}
            </div>
            <div className="min-w-0 rounded-lg border border-border bg-secondary/20 p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Provider branches</p>
              <p className="mt-1 font-medium">{providerBranches.length || 'None'}</p>
              {providerBranches.length > 0 && (
                <p className="mt-1 break-words text-xs text-muted-foreground">
                  {providerOnlyBranches.length > 0
                    ? `${providerOnlyBranches.length} not mirrored locally`
                    : 'Local refs cover linked remote branches'}
                </p>
              )}
            </div>
          </div>

          {(history.remotes.length > 0 || history.worktrees.length > 0 || dirtyCount > 0 || providerBranches.length > 0) && (
            <div className="space-y-3 rounded-lg border border-border bg-secondary/10 p-4">
              {history.remotes.length > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Remotes</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {history.remotes.map((remote) => (
                      <span key={remote.name} className="max-w-full truncate rounded-full border border-border px-2 py-1 text-xs">
                        {remote.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {history.worktrees.length > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Active worktrees</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {history.worktrees.map((worktree) => (
                      <span
                        key={`${worktree.path}:${worktree.branch || worktree.headSha || 'detached'}`}
                        className={`max-w-full truncate rounded-full border px-2 py-1 text-xs ${
                          worktree.current ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border'
                        }`}
                      >
                        {worktree.branch || worktree.displayPath}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {providerBranches.length > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Linked remote branches</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {providerBranches.slice(0, 16).map((branch) => {
                      const canOpenReview = reviewableBranchNames.has(branch.name)
                      return (
                        <span
                          key={`${branch.name}:${branch.commitSha || 'none'}`}
                          className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-1 text-xs ${providerBranchBadgeClass(branch)}`}
                        >
                          <span className="min-w-0 truncate">{branch.name}</span>
                          {canOpenReview && (
                            <button
                              type="button"
                              onClick={() => void handleOpenBranchReview(branch)}
                              disabled={!!branchAction}
                              className="ml-1 inline-flex items-center gap-1 rounded-full border border-current/20 px-1.5 py-0.5 text-[10px] transition-colors hover:bg-background/60 disabled:opacity-50"
                            >
                              {branchAction === branch.name ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <GitPullRequestArrow className="h-3 w-3" />
                              )}
                              {reviewLabel}
                            </button>
                          )}
                        </span>
                      )
                    })}
                    {providerBranches.length > 16 && (
                      <span className="rounded-full border border-border px-2 py-1 text-xs text-muted-foreground">
                        +{providerBranches.length - 16} more
                      </span>
                    )}
                  </div>
                  {reviewableBranchNames.size > 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {reviewLabel} is shown only for branches with commits not already contained in {baseBranchName}.
                    </p>
                  )}
                  {providerOnlyBranches.length > 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {providerOnlyBranches.length} linked remote branches do not currently exist as local remote refs.
                    </p>
                  )}
                </div>
              )}

              {dirtyCount > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Uncommitted changes</p>
                  <div className="mt-2 space-y-1 font-mono text-xs text-muted-foreground">
                    {history.statusLines.slice(0, 8).map((line) => (
                      <div key={line}>{line}</div>
                    ))}
                    {history.statusLines.length > 8 && (
                      <div>… {history.statusLines.length - 8} more</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="overflow-hidden rounded-lg border border-border bg-card">
            {history.commits.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">No commits yet.</div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                  <div>
                    <h4 className="text-sm font-semibold">Commits</h4>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Same history shown as topology or chronological rows.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-lg border border-border bg-secondary/40 p-1">
                      <button
                        type="button"
                        onClick={() => setActiveView('graph')}
                        className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                          activeView === 'graph' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Graph
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveView('timeline')}
                        className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                          activeView === 'timeline' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Timeline
                      </button>
                    </div>
                    <span className="rounded-full border border-border px-2 py-1 text-xs text-muted-foreground">
                      {history.commits.length} commits shown
                    </span>
                  </div>
                </div>
                {activeView === 'graph' ? (
                  <GitHistoryGraph
                    commits={history.commits}
                    providerBranchHeads={providerBranchHeads}
                    selectedSha={selectedCommit?.sha || null}
                    lastSeenSha={binding?.lastSeenSha}
                    lastDeployedSha={binding?.lastDeployedSha}
                    onSelectSha={setSelectedSha}
                  />
                ) : (
                  <div>
                    {history.commits.length > TIMELINE_PAGE_SIZE && (
                      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-secondary/10 px-4 py-3 text-xs text-muted-foreground">
                        <span>
                          Showing {timelinePageStart + 1}-{Math.min(timelinePageStart + TIMELINE_PAGE_SIZE, history.commits.length)} of{' '}
                          {history.commits.length}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setTimelinePage((current) => Math.max(1, current - 1))}
                            disabled={timelinePage <= 1}
                            className="rounded-md border border-border px-2 py-1 transition-colors hover:bg-secondary disabled:opacity-50"
                          >
                            Previous
                          </button>
                          <span>Page {timelinePage} / {timelinePageCount}</span>
                          <button
                            type="button"
                            onClick={() => setTimelinePage((current) => Math.min(timelinePageCount, current + 1))}
                            disabled={timelinePage >= timelinePageCount}
                            className="rounded-md border border-border px-2 py-1 transition-colors hover:bg-secondary disabled:opacity-50"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="divide-y divide-border">
                    {timelineCommits.map((commit: GitHistoryCommit) => {
                      const isSelected = selectedCommit?.sha === commit.sha
                      const isDeployed = binding?.lastDeployedSha === commit.sha
                      const isSeen = binding?.lastSeenSha === commit.sha
                      const linkedRemoteBranches = providerBranchHeads.get(commit.sha) || []

                      return (
                        <div key={commit.sha} className="bg-card">
                          <button
                            type="button"
                            onClick={() => setSelectedSha(commit.sha)}
                            className={`w-full px-4 py-3 text-left transition-colors hover:bg-secondary/30 ${
                              isSelected ? 'bg-primary/5' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="truncate font-medium">{commit.subject || '(no subject)'}</p>
                                  {commit.parents.length > 1 && (
                                    <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-500">
                                      Merge
                                    </span>
                                  )}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {commit.decorations.slice(0, 3).map((decoration) => (
                                    <span
                                      key={`${commit.sha}-${decoration}`}
                                      className={`rounded-full border px-2 py-0.5 text-[11px] ${decorationBadgeClass(decoration)}`}
                                    >
                                      {decoration}
                                    </span>
                                  ))}
                                  {commit.decorations.length > 3 && (
                                    <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                                      +{commit.decorations.length - 3} refs
                                    </span>
                                  )}
                                  {linkedRemoteBranches.slice(0, 2).map((branch) => (
                                    <span
                                      key={`${commit.sha}-provider-${branch.name}`}
                                      className={`max-w-full truncate rounded-full border px-2 py-0.5 text-[11px] ${providerBranchBadgeClass(branch)}`}
                                    >
                                      {branch.default ? `remote:${branch.name} default` : `remote:${branch.name}`}
                                    </span>
                                  ))}
                                  {linkedRemoteBranches.length > 2 && (
                                    <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                                      +{linkedRemoteBranches.length - 2} remote
                                    </span>
                                  )}
                                  {isDeployed && (
                                    <span className="rounded-full border border-green-500/30 bg-green-500/10 px-2 py-0.5 text-[11px] text-green-500">
                                      Deployed
                                    </span>
                                  )}
                                  {isSeen && !isDeployed && (
                                    <span className="rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-[11px] text-blue-500">
                                      Linked HEAD
                                    </span>
                                  )}
                                </div>
                                <p className="mt-2 text-xs text-muted-foreground">
                                  {commit.authorName} • {formatRelativeTime(commit.authoredAt)}
                                </p>
                              </div>
                              <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-1 font-mono text-[11px] text-muted-foreground">
                                <GitCommitHorizontal className="w-3 h-3" />
                                {commit.shortSha}
                              </span>
                            </div>
                          </button>
                        </div>
                      )
                    })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {selectedCommit && (
            <div className="min-w-0 rounded-lg border border-border bg-secondary/10 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-sm font-semibold">{selectedCommit.subject || '(no subject)'}</h4>
                    {selectedCommit.parents.length > 1 && (
                      <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-500">
                        Merge commit
                      </span>
                    )}
                    {binding?.lastDeployedSha === selectedCommit.sha && (
                      <span className="rounded-full border border-green-500/30 bg-green-500/10 px-2 py-0.5 text-[11px] text-green-500">
                        Deployed
                      </span>
                    )}
                    {binding?.lastSeenSha === selectedCommit.sha && binding?.lastDeployedSha !== selectedCommit.sha && (
                      <span className="rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-[11px] text-blue-500">
                        Linked HEAD
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {selectedCommit.authorName} • {formatAbsoluteTime(selectedCommit.authoredAt)}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-1 font-mono text-[11px] text-muted-foreground">
                  <GitCommitHorizontal className="w-3 h-3" />
                  {selectedCommit.shortSha}
                </span>
              </div>

              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Commit</p>
                  <p className="mt-1 font-mono text-xs break-all">{selectedCommit.sha}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Author</p>
                  <p className="mt-1 text-sm">
                    {selectedCommit.authorName}
                    {selectedCommit.authorEmail ? ` <${selectedCommit.authorEmail}>` : ''}
                  </p>
                </div>
              </div>

              {(selectedCommit.decorations.length > 0 || (providerBranchHeads.get(selectedCommit.sha) || []).length > 0) && (
                <div className="mt-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">References</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedCommit.decorations.map((decoration) => (
                      <span
                        key={`${selectedCommit.sha}-${decoration}`}
                        className={`rounded-full border px-2 py-1 text-[11px] ${decorationBadgeClass(decoration)}`}
                      >
                        {decoration}
                      </span>
                    ))}
                    {(providerBranchHeads.get(selectedCommit.sha) || []).map((branch) => (
                      <span
                        key={`${selectedCommit.sha}-provider-${branch.name}`}
                        className={`rounded-full border px-2 py-1 text-[11px] ${providerBranchBadgeClass(branch)}`}
                      >
                        {branch.default ? `remote:${branch.name} default` : `remote:${branch.name}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedCommit.parents.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Parents</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedCommit.parents.map((parent) => (
                      <span key={parent} className="rounded-full border border-border px-2 py-1 font-mono text-[11px]">
                        {parent.slice(0, 12)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Description</p>
                <pre className="mt-2 whitespace-pre-wrap rounded-lg border border-border bg-card p-3 text-xs text-muted-foreground">
                  {selectedCommit.body || selectedCommit.subject || 'No extended commit description.'}
                </pre>
              </div>
            </div>
          )}

        </>
      )}
    </div>
  )
}
